import * as React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { artikelGruppenByNrSelector, lookupsLoadingFinishedSelector } from 'store/lookups/selector';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { RecordViewUrlParams } from 'layout/App/types';
import { createRecordUrl } from 'utils/formatter';
import { Fabric, TextField } from '@fluentui/react';
import { viewStyles } from 'styles/viewStyles';
import { Helmet } from 'react-helmet';
import { ArtikelGruppeByNr } from 'formComponents/lookups/ArtikelGruppeByNr';
import { ArtikelDialog } from 'components/Lager/ArtikelDetail/ArtikelDialog';
import { getArtikelDetailId, splitArtikelDetailId } from 'utils/helper';
import { ConfirmMessage } from 'formComponents/ConfirmMessage/ConfirmMessage';
import { ArtikelDetailSearch, ArtikelDetailSearchHandle } from 'components/Lager/ArtikelDetail/ArtikelDetailSearch';
import { addArtikel, Artikel, defaultArtikel, getArtikel } from 'api/artikel';
import { formStyles } from 'styles/formStyles';
import { addArtikelDetail, ArtikelDetail, defaultArtikelDetail, getArtikelDetail } from 'api/artikelDetail';

const styles = mergeStyleSets({
    searchBar: {
        display: 'grid',
        gridTemplateColumns: '200px 200px max-content 1fr max-content max-content',
        gridColumnGap: 10,
        marginTop: 10,
    },
    searchBarSmall1: {
        display: 'grid',
        gridTemplateColumns: '200px 200px max-content 1fr max-content max-content',
        gridColumnGap: 10,
        marginTop: 10,
    },
    searchBarSmall2: {
        display: 'grid',
        gridTemplateColumns: '150px 150px',
        gridColumnGap: 10,
        marginTop: 10,
    },
    newArtikelContainer: {
        height: 350,
    },
    editRow: {
        display: 'grid',
        gridTemplateColumns: '250px 1fr',
        gridColumnGap: 10,
    },
});

interface ArtikelDialogData {
    artikelGruppeNr: number;
    artikelNr: string;
}

interface NewArtikelData {
    show: boolean;
    artikelGruppeNr: number | null;
    artikelNr: string | null;
}

const defaultNewArtikelData: NewArtikelData = {
    show: false,
    artikelGruppeNr: null,
    artikelNr: null,
};

export const ArtikelView: React.FC = () => {
    const history = useHistory();
    const lookupsLoadingFinished = useSelector(lookupsLoadingFinishedSelector);
    const artikelGruppenByNr = useSelector(artikelGruppenByNrSelector);
    const [newArtikelData, setNewArtikelData] = React.useState<NewArtikelData>({ ...defaultNewArtikelData });
    const artikelDetailSearchRef = React.useRef<ArtikelDetailSearchHandle>(null);

    const { urlRecordNr: encodedurlRecordNr } = useParams<RecordViewUrlParams>();
    const urlRecordNr = decodeURIComponent(encodedurlRecordNr || '');

    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);

    const artikelDialogData = React.useMemo<ArtikelDialogData | null>(() => {
        return splitArtikelDetailId(urlRecordNr);
    }, [urlRecordNr]);

    if (!lookupsLoadingFinished) {
        return <div />;
    }

    return (
        <Fabric className={viewStyles.containerWithoutHeader}>
            <Helmet>
                <title>
                    Artikel{' '}
                    {(artikelDialogData?.artikelGruppeNr
                        ? artikelGruppenByNr[artikelDialogData?.artikelGruppeNr]?.text ||
                          artikelDialogData?.artikelGruppeNr
                        : '') + (artikelDialogData?.artikelNr ? ' | ' + artikelDialogData?.artikelNr : '')}
                </title>
            </Helmet>

            <ArtikelDetailSearch
                ref={artikelDetailSearchRef}
                parentView="ArtikelDetailSelect"
                onNew={() => {
                    setNewArtikelData({ ...defaultNewArtikelData, show: true });
                }}
                onEdit={(artikelDetail) => {
                    const id = getArtikelDetailId(artikelDetail.artikelGruppeNr, artikelDetail.artikelNr);
                    history.push(createRecordUrl('lager/artikel', undefined, id, undefined, false));
                }}
            />

            {artikelDialogData !== null && (
                <ArtikelDialog
                    artikelGruppeNr={artikelDialogData.artikelGruppeNr}
                    artikelNr={artikelDialogData.artikelNr}
                    onClose={() => {
                        history.push(createRecordUrl('lager/artikel'));
                    }}
                    refetch={() => {
                        artikelDetailSearchRef.current?.refetch();
                        // artikelEigenschaftLinkList.refetch().then(() => {
                        //     artikelDetailList.refetch().then();
                        // });
                    }}
                />
            )}
            {newArtikelData.show && (
                <ConfirmMessage
                    title="Neuen Artikel erstellen"
                    message="Ein neuer Artikel wird in einer Artikelgruppe angelegt."
                    minWidth={800}
                    confirmText="Artikel erstellen"
                    confirmDisabled={
                        newArtikelData.artikelGruppeNr === defaultNewArtikelData.artikelGruppeNr ||
                        newArtikelData.artikelNr === defaultNewArtikelData.artikelNr
                    }
                    cancelText="Abbruch"
                    content={
                        <div className={styles.newArtikelContainer}>
                            <ul>
                                <li>
                                    Wenn noch kein Artikel mit der angegebenen Nummer existiert, wird dieser angelegt.
                                </li>
                                <li>
                                    Existiert bereits ein Artikel mit dieser Nummer, wird dieser nur mit der
                                    Artikelgruppe verknüpft.
                                </li>
                            </ul>
                            <div className={styles.editRow}>
                                <div>
                                    <ArtikelGruppeByNr
                                        value={newArtikelData.artikelGruppeNr}
                                        disabled={false}
                                        label="Artikelgruppe"
                                        renderLabel="label"
                                        onChange={(newValue): void => {
                                            setNewArtikelData({
                                                ...newArtikelData,
                                                artikelGruppeNr: newValue,
                                            });
                                        }}
                                    />
                                </div>
                                <TextField
                                    label="Artikel-Nr."
                                    value={newArtikelData.artikelNr || ''}
                                    onChange={(event, newValue): void => {
                                        setNewArtikelData({
                                            ...newArtikelData,
                                            artikelNr: newValue || '',
                                        });
                                    }}
                                />
                            </div>
                            {error && <div className={formStyles.error}>{error}</div>}
                        </div>
                    }
                    onConfirm={async () => {
                        if (saving) {
                            return;
                        }

                        if (!newArtikelData.artikelGruppeNr || !newArtikelData.artikelNr) {
                            setSaving(false);
                            return;
                        }

                        setSaving(true);
                        setError(null);
                        const newArtikelGruppeNr = newArtikelData.artikelGruppeNr;
                        const newArtikelNr = newArtikelData.artikelNr.trim();

                        let artikel: Artikel | null;
                        try {
                            artikel = await getArtikel({ artikelNr: newArtikelNr });
                            console.info('-> found');
                        } catch {
                            artikel = null;
                            console.info('-> not found');
                        }

                        if (!artikel) {
                            artikel = {
                                ...defaultArtikel,
                                nr: newArtikelNr,
                            };

                            try {
                                await addArtikel(artikel);
                            } catch {
                                setSaving(false);
                                setError('Fehler beim Speichern (Artikel).');
                            }
                        }

                        let artikelDetail: ArtikelDetail | null = null;
                        try {
                            artikelDetail = await getArtikelDetail({
                                artikelGruppeNr: newArtikelGruppeNr,
                                artikelNr: newArtikelNr,
                            });
                            console.info('-> artikelDetail found');
                        } catch {
                            artikel = null;
                            console.info('-> artikelDetail not found');
                        }

                        if (artikelDetail) {
                            setError('Dieser Artikel existiert in dieser Gruppe.');
                            return;
                        }

                        artikelDetail = {
                            ...defaultArtikelDetail,
                            artikelGruppeNr: newArtikelGruppeNr,
                            artikelNr: newArtikelNr,
                        };

                        try {
                            await addArtikelDetail(artikelDetail);
                        } catch {
                            setSaving(false);
                            setError('Fehler beim Speichern (ArtikelDetail.');
                            return;
                        }

                        artikelDetailSearchRef.current?.refetch();
                        setSaving(false);
                        setNewArtikelData(defaultNewArtikelData);

                        const id = getArtikelDetailId(newArtikelGruppeNr, newArtikelNr);
                        history.push(createRecordUrl('lager/artikel', undefined, id, undefined, false));
                    }}
                    onCancel={() => setNewArtikelData({ ...defaultNewArtikelData })}
                />
            )}
        </Fabric>
    );
};
